import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Phone, Mail, Clock } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/contact`, { name, email, phone: phone || null, message, sms_consent: smsConsent });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
      setSmsConsent(false);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '215-770-0679',
      subtext: 'Mon-Fri 8am-8pm, Sat-Sun 9am-5pm'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@phillyrideshare.com',
      subtext: 'We respond within 24 hours'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Monday - Friday',
      subtext: '8:00 AM - 8:00 PM'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <Navbar />

      <section className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Contact Us
            </h1>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Have questions? We'd love to hear from you. Send us a message and we'll 
              respond as soon as possible.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white" data-testid="contact-form-card">
                <CardHeader>
                  <CardTitle>Send us a Message</CardTitle>
                  <CardDescription>Fill out the form and we'll get back to you shortly</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          placeholder="Your name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          data-testid="contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone (Optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        placeholder="How can we help you?"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={5}
                        data-testid="contact-message"
                      />
                    </div>
                    <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <Checkbox
                        id="sms-consent"
                        checked={smsConsent}
                        onCheckedChange={(v) => setSmsConsent(v === true)}
                        className="mt-0.5"
                        data-testid="contact-sms-consent"
                      />
                      <Label htmlFor="sms-consent" className="text-xs font-normal leading-relaxed text-slate-500 cursor-pointer">
                        By checking this box, I agree to receive recurring text messages from Fair Rideshare LLC at
                        the phone number provided, including booking updates, payment reminders, and promotional
                        offers. Consent is not a condition of any purchase. Message frequency varies. Message &amp;
                        data rates may apply. Reply STOP to opt out or HELP for help. See our{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-600 font-semibold hover:underline">Privacy Policy</a>{' '}
                        and{' '}
                        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-amber-600 font-semibold hover:underline">Terms of Service</a>.
                      </Label>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      disabled={loading}
                      data-testid="contact-submit"
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              {contactInfo.map((item, index) => (
                <Card key={index} className="bg-white" data-testid={`contact-info-${index}`}>
                  <CardContent className="p-4 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-slate-700">{item.content}</p>
                      <p className="text-sm text-slate-500">{item.subtext}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
